import { useState, useEffect, useRef, useMemo, startTransition } from 'react'
import { useDecision } from '@optimizely/react-sdk'
import { useCinematicsStore } from '@/cinematics/stores/CinematicsStore'
import { fetchCinematics } from '@/services/fetch-cinematics'
import { mergeCinematics } from '@/cinematics/utils/mergeCinematics'
import { useUser } from '@/hooks/users/useUser'
import { clarity, CLARITY_TAGS } from '@/utils/clarity'

import settings from '@/settings/public'

// Hard upper bound on how long the AB gate may hold the loading overlay open.
// Set well above the OptimizelyProvider timeout (500ms) AND any realistic
// fetchCinematics duration so it never cuts off a slow-but-working flow (which
// would cause a base->variant content flash). It is a last-resort anti-hang net
// for the cases the clientReady/didTimeout signals don't cover: the decision
// never resolving (NO_CLIENT) or fetchCinematics stalling forever with no reject.
export const AB_DECISION_MAX_WAIT_MS = 8000

export function useABDecision() {
  const { applyABCinematics, isUsingLocalCinematics } = useCinematicsStore()
  const { profile } = useUser()

  const [decision, isDecisionReady, didTimeout] = useDecision('cinematics_tag', {}, {
    overrideUserId: profile?._id,
  })
  const isDecisionEnabled = decision.enabled
  const originalTag = settings?.api?.cinematicsTag

  const experimentTag = isDecisionEnabled
    ? (decision.variables?.cinematics as string | undefined)
    : undefined

  const needsFetch = !!experimentTag && experimentTag !== originalTag && !isUsingLocalCinematics
  const [isApplied, setIsApplied] = useState(false)

  const hasRunRef = useRef(false)

  // Tag Clarity sessions with the AB variant so recordings can be filtered
  // per experiment arm.
  useEffect(() => {
    if (isDecisionReady && isDecisionEnabled && experimentTag) {
      clarity.set(CLARITY_TAGS.AB_VARIANT, experimentTag)
    }
  }, [isDecisionReady, isDecisionEnabled, experimentTag])

  useEffect(() => {
    // The Optimizely client can fail to become ready before the provider timeout
    // (datafile blocked by an adblocker, slow network, CDN unreachable). On timeout
    // clientReady stays false while didTimeout latches true. We must unblock the
    // loading overlay on EITHER signal, otherwise isApplied never flips and a
    // logged-in user is stuck (waitingForAB) — common after the SSO
    // signInWithRedirect full-page reload, where the client inits from a cold cache.
    if (!isDecisionReady && !didTimeout) return

    // Timed out but the datafile is still not loaded: we can't make a real decision
    // yet. Mark applied so the overlay clears with the base cinematic. didTimeout
    // latches true, but clientReady can flip true later — when it does, this effect
    // re-runs (isDecisionReady dep) and applies the real AB decision below. So we
    // must NOT gate the apply branch on didTimeout, or slow-datafile users would
    // permanently lose their variant.
    if (!isDecisionReady) {
      startTransition(() => setIsApplied(true))
      return
    }

    if (!needsFetch) {
      startTransition(() => setIsApplied(true))
      return
    }

    if (!hasRunRef.current) {
      hasRunRef.current = true
      fetchCinematics({ tag: experimentTag }).then((abCinematics) => {
        const { cinematics: current, originalCinematics: original } = useCinematicsStore.getState()
        const brandingIsApplied = current !== original
        if (!brandingIsApplied) {
          const merged = mergeCinematics({ base: original, override: abCinematics })
          applyABCinematics(merged, experimentTag)
        }
        startTransition(() => setIsApplied(true))
      }).catch(() => {
        startTransition(() => setIsApplied(true))
      })
    }
  }, [experimentTag, originalTag, applyABCinematics, isDecisionReady, didTimeout, isDecisionEnabled, isUsingLocalCinematics, needsFetch])

  // Last-resort safety net for any path where isApplied never settles: the
  // Optimizely decision never resolving (NO_CLIENT), or fetchCinematics stalling
  // with no resolve/reject. Force-applies after a bounded wait so the overlay
  // can't hang. This only flips the overlay gate — it never touches Optimizely
  // bucketing/exposure, and never sets hasRunRef, so a pending/late AB fetch
  // still applies its variant afterwards (experiments are unaffected).
  useEffect(() => {
    if (isApplied) return
    const timer = setTimeout(() => {
      startTransition(() => setIsApplied(true))
    }, AB_DECISION_MAX_WAIT_MS)
    return () => clearTimeout(timer)
  }, [isApplied])

  return useMemo(() => ({
    isDecisionReady,
    isDecisionEnabled,
    experimentTag,
    originalTag,
    isApplied
  }), [isDecisionReady, isDecisionEnabled, experimentTag, originalTag, isApplied])
}
